import { ModuleLayout } from '@billy/design-system'
import { ReactElement, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  EXPERT_REPORT_COMPARE_WITH,
  ExpertReportHeaderActions,
  ExpertReportPeriodPicker,
} from '@features/expertReports'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import type { PeriodMode } from '../../enums/periodMode'
import type { PeriodValue } from '../../types/periodValue'
import { BalanceSheetControlCallout } from './elements/BalanceSheetControlCallout'
import { ExpertBalanceSheetTable } from './elements/ExpertBalanceSheetTable'
import { ExpertBalanceSheetToolbar } from './elements/ExpertBalanceSheetToolbar'
import { useExpertBalanceSheetPeriod } from './hooks/useExpertBalanceSheetPeriod'
import { useFetchExpertBalanceSheet } from './hooks/useFetchExpertBalanceSheet'
import { EXPERT_BALANCE_SHEET_COMPARE_COUNT } from './query-api'
import { OverviewContent } from './styles'
import type { ExpertBalanceSheetReport } from './types/expertBalanceSheetReport'
import { ExpertBalanceSheetTab, ExpertBalanceSheetViewMode } from './types/expertBalanceSheetView'
import {
  isBalanceSheetReportEmpty,
  selectBalanceSheetAssetsView,
  selectBalanceSheetControl,
  selectBalanceSheetLiabilitiesView,
} from './utils/selectBalanceSheetView'

interface ExpertBalanceSheetContentProps {
  encodedPeriod?: string
  isError?: boolean
  isFetchingNewPeriod?: boolean
  isLoading?: boolean
  periodType?: PeriodMode
  report?: ExpertBalanceSheetReport
  setPeriod?: (value: PeriodValue) => boolean
}

export const ExpertBalanceSheetContent = ({
  encodedPeriod = '',
  isError = false,
  isFetchingNewPeriod = false,
  isLoading = false,
  periodType,
  report,
  setPeriod = () => false,
}: ExpertBalanceSheetContentProps): ReactElement => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<ExpertBalanceSheetTab>(ExpertBalanceSheetTab.Assets)
  const [viewMode, setViewMode] = useState<ExpertBalanceSheetViewMode>(ExpertBalanceSheetViewMode.Summary)

  const assetsView = useMemo(() => selectBalanceSheetAssetsView(report, viewMode), [report, viewMode])
  const liabilitiesView = useMemo(() => selectBalanceSheetLiabilitiesView(report, viewMode), [report, viewMode])
  const control = useMemo(() => selectBalanceSheetControl(assetsView, liabilitiesView), [assetsView, liabilitiesView])
  const isEmpty = useMemo(() => isBalanceSheetReportEmpty(assetsView, liabilitiesView), [assetsView, liabilitiesView])

  const imbalanceToReport = !isLoading && !isError && !isEmpty && control && !control.isBalanced ? control : undefined
  const sideActions = useMemo(
    () => <ExpertReportPeriodPicker encodedPeriod={encodedPeriod} onChange={setPeriod} />,
    [encodedPeriod, setPeriod],
  )

  const headerBottomActions = (
    <ExpertReportHeaderActions>
      <ExpertBalanceSheetToolbar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onViewModeChange={setViewMode}
        viewMode={viewMode}
      />
      {imbalanceToReport && <BalanceSheetControlCallout imbalance={imbalanceToReport.imbalance} />}
    </ExpertReportHeaderActions>
  )

  return (
    <ModuleLayout
      fullHeight
      fullWidth
      headerBottomActions={headerBottomActions}
      sideActions={sideActions}
      title={t('expert_balance_sheet.title')}
    >
      <OverviewContent>
        {activeTab === ExpertBalanceSheetTab.Assets ? (
          <ExpertBalanceSheetTable
            isEmpty={isEmpty}
            isError={isError}
            isLoading={isLoading || isFetchingNewPeriod}
            periods={assetsView.periods}
            periodType={periodType}
            rows={assetsView.rows}
            tab={ExpertBalanceSheetTab.Assets}
          />
        ) : (
          <ExpertBalanceSheetTable
            isEmpty={isEmpty}
            isError={isError}
            isLoading={isLoading || isFetchingNewPeriod}
            periods={liabilitiesView.periods}
            periodType={periodType}
            rows={liabilitiesView.rows}
            tab={ExpertBalanceSheetTab.LiabilitiesAndEquity}
          />
        )}
      </OverviewContent>
    </ModuleLayout>
  )
}

export const ExpertBalanceSheet = (): ReactElement => {
  const { organization } = useUserOrganization()
  const { encodedPeriod, periodType, setPeriod } = useExpertBalanceSheetPeriod()
  const { isError, isFetchingNewPeriod, isLoading, report } = useFetchExpertBalanceSheet({
    compareCount: EXPERT_BALANCE_SHEET_COMPARE_COUNT,
    compareWith: EXPERT_REPORT_COMPARE_WITH,
    organizationId: organization?.id ?? '',
    period: encodedPeriod,
  })

  return (
    <ExpertBalanceSheetContent
      encodedPeriod={encodedPeriod}
      isError={isError}
      isFetchingNewPeriod={isFetchingNewPeriod}
      isLoading={isLoading}
      periodType={periodType}
      report={report}
      setPeriod={setPeriod}
    />
  )
}
